import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { getMembersDB, saveMemberDB, saveInvoiceDB, getNextMemberId, getGelanggangDB } from '../../services/db';
import { Gelanggang, UserProfile, GelanggangDetail } from '../../types';
import { useToast } from '../../components/common/Toast';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { saveViewScroll, restoreViewScroll } from '../../utils/scrollRestoration';
import {
  Users,
  Plus,
  Link as LinkIcon,
  User,
  Calendar,
  MapPin,
  Clock,
  Shield,
  Mail,
  Phone,
  Fingerprint,
  Edit2,
  Save,
  ArrowLeft,
  Camera,
  Upload,
  Loader2,
  Trash2,
  Building,
  CreditCard,
  Sparkles,
  CheckCircle2,
  Ruler
} from 'lucide-react';
import { UniformSizeChartModal } from '../../components/common/UniformSizeChartModal';
import { BengkungCulaBadge } from '../../components/common/BengkungCulaBadge';
import { getSilatRankFromBengkung } from '../../data/bengkungRanks';
import { uploadMemberPhotoToAhliDriveJson } from '../../lib/gdrive';
import { isValidEmail, sanitizeIc, sanitizePhone, sanitizeMemberId, getIcDetails, isChildOfParent, findDuplicateIcMember, findDuplicatePhoneMember, toTitleCase, getHighestRole, isSuperAdmin } from '../../utils/formatters';
import { getGelanggangClassPrice, parseGelanggangClasses, resolveMemberEnrollments, createOnboardingInvoice, UNIFORM_SIZES, getUniformPriceBySize, isEligibleForMonthlyFee, getMonthlyFeeExemptionReason } from '../../utils/pricing';

export const KeluargaPage: React.FC = () => {
  const { currentUser, setCurrentUser } = useAuth();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [children, setChildren] = React.useState<UserProfile[]>([]);
  const [gelanggangList, setGelanggangList] = React.useState<GelanggangDetail[]>([]);

  // Modals state
  const [isAddChildModalOpen, setIsAddChildModalOpen] = React.useState<boolean>(false);
  const [isLinkChildModalOpen, setIsLinkChildModalOpen] = React.useState<boolean>(false);

  // Add child form states
  const [addChildName, setAddChildName] = React.useState('');
  const [addChildIc, setAddChildIc] = React.useState('');
  const [addChildPhone, setAddChildPhone] = React.useState('');
  const [addChildEmail, setAddChildEmail] = React.useState('');
  const [addChildAddress, setAddChildAddress] = React.useState(currentUser?.address || '');
  const [addChildGelanggang, setAddChildGelanggang] = React.useState<Gelanggang>(
    currentUser?.gelanggang && currentUser.gelanggang !== 'Semua Gelanggang'
      ? currentUser.gelanggang
      : 'Tiada' as Gelanggang
  );
  const [addChildSessionTime, setAddChildSessionTime] = React.useState('Semua Sesi');
  const [addChildUniformSize, setAddChildUniformSize] = React.useState('130cm');
  const [isSizeChartOpen, setIsSizeChartOpen] = React.useState(false);
  const [addChildRelationship, setAddChildRelationship] = React.useState('Anak');

  React.useEffect(() => {
    if (currentUser?.address && !addChildAddress) {
      setAddChildAddress(currentUser.address);
    }
  }, [currentUser]);

  const [linkChildIc, setLinkChildIc] = React.useState('');
  const [linkChildRelationship, setLinkChildRelationship] = React.useState('Anak');

  // Edit child form states
  const [editingChild, setEditingChild] = React.useState<UserProfile | null>(null);
  const [editName, setEditName] = React.useState('');
  const [editIc, setEditIc] = React.useState('');
  const [editPhone, setEditPhone] = React.useState('');
  const [editEmail, setEditEmail] = React.useState('');
  const [editAddress, setEditAddress] = React.useState('');
  const [editGelanggang, setEditGelanggang] = React.useState<Gelanggang>('Pusaka Warisan');
  const [editSessionTime, setEditSessionTime] = React.useState('');
  const [editPhotoUrl, setEditPhotoUrl] = React.useState('/default-avatar.svg');
  const [editRelationship, setEditRelationship] = React.useState('Anak');
  const [isUploadingEditPhoto, setIsUploadingEditPhoto] = React.useState(false);
  const editPhotoInputRef = React.useRef<HTMLInputElement | null>(null);

  // Enrollment configuration
  interface Enrollment {
    gelanggang: string;
    plans: string[];
  }

  const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = React.useState<boolean>(false);
  const [selectedEnrollmentUser, setSelectedEnrollmentUser] = React.useState<UserProfile | null>(null);
  const [currentEnrollments, setCurrentEnrollments] = React.useState<Enrollment[]>([]);
  const [newEnrollmentGelanggang, setNewEnrollmentGelanggang] = React.useState<Gelanggang>('Pusaka Warisan');
  const [newEnrollmentPlans, setNewEnrollmentPlans] = React.useState<string[]>(['Silat']);

  const getPlanPrice = React.useCallback((gelName: string, planName: string): number => {
    return getGelanggangClassPrice(gelName, planName, gelanggangList);
  }, [gelanggangList]);

  const parseEnrollments = React.useCallback((user: UserProfile): Enrollment[] => {
    return resolveMemberEnrollments(user, gelanggangList);
  }, [gelanggangList]);

  // Calculate total monthly commitment across family, respecting role fee exemptions
  const totalFamilyMonthlyFee = React.useMemo(() => {
    let total = 0;
    if (currentUser && isEligibleForMonthlyFee(currentUser)) {
      const userEns = resolveMemberEnrollments(currentUser, gelanggangList);
      userEns.forEach(en => {
        en.plans.forEach(p => {
          total += getGelanggangClassPrice(en.gelanggang, p, gelanggangList);
        });
      });
    }
    children.forEach(c => {
      if (isEligibleForMonthlyFee(c)) {
        const childEns = resolveMemberEnrollments(c, gelanggangList);
        childEns.forEach(en => {
          en.plans.forEach(p => {
            total += getGelanggangClassPrice(en.gelanggang, p, gelanggangList);
          });
        });
      }
    });
    return total;
  }, [currentUser, children, gelanggangList]);

  // Check if currentUser or any family member has monthly fee exemption
  const hasExemptFamilyMember = React.useMemo(() => {
    if (currentUser && !isEligibleForMonthlyFee(currentUser)) return true;
    return children.some(c => !isEligibleForMonthlyFee(c));
  }, [currentUser, children]);

  const handleOpenEnrollmentModal = (user: UserProfile) => {
    setSelectedEnrollmentUser(user);
    const existing = parseEnrollments(user);
    setCurrentEnrollments(existing);

    const validGels = gelanggangList.filter(g => g.name !== 'Tiada' && g.name !== 'Semua Gelanggang');
    const defaultGel = (user.gelanggang && user.gelanggang !== 'Tiada' && user.gelanggang !== 'Semua Gelanggang')
      ? user.gelanggang
      : (validGels[0]?.name || 'Pusaka Warisan');

    setNewEnrollmentGelanggang(defaultGel);
    const classes = parseGelanggangClasses(defaultGel, gelanggangList.find(g => g.name === defaultGel)?.classes);
    if (classes.length > 0) {
      setNewEnrollmentPlans([classes[0].name]);
    } else {
      setNewEnrollmentPlans(['Silat']);
    }
    setIsEnrollmentModalOpen(true);
  };

  const handleAddEnrollment = () => {
    if (newEnrollmentGelanggang === 'Tiada') {
      showToast('error', 'Gelanggang Tidak Sah', 'Sila pilih gelanggang latihan yang sah.');
      return;
    }
    if (currentEnrollments.some(e => e.gelanggang === newEnrollmentGelanggang)) {
      showToast('error', 'Gelanggang Telah Wujud', `Ahli sudah didaftarkan di gelanggang ${newEnrollmentGelanggang}.`);
      return;
    }
    if (newEnrollmentPlans.length === 0) {
      showToast('error', 'Sila Pilih Kelas', 'Sila pilih sekurang-kurangnya satu kelas latihan.');
      return;
    }
    const newEn: Enrollment = {
      gelanggang: newEnrollmentGelanggang,
      plans: [...newEnrollmentPlans]
    };
    setCurrentEnrollments([...currentEnrollments, newEn]);
  };

  const handleRemoveEnrollment = (gelName: string) => {
    setCurrentEnrollments(currentEnrollments.filter(e => e.gelanggang !== gelName));
  };

  const handleTogglePlanInEnrollment = (gelName: string, planName: string) => {
    setCurrentEnrollments(currentEnrollments.map(en => {
      if (en.gelanggang === gelName) {
        const hasPlan = en.plans.includes(planName);
        let updatedPlans = hasPlan
          ? en.plans.filter(p => p !== planName)
          : [...en.plans, planName];

        // Mutually exclusive silat classes if multiple silat types exist (e.g. Dewasa vs Muda)
        if (planName.toLowerCase().includes('dewasa') && !hasPlan) {
          updatedPlans = updatedPlans.filter(p => !p.toLowerCase().includes('muda'));
        } else if (planName.toLowerCase().includes('muda') && !hasPlan) {
          updatedPlans = updatedPlans.filter(p => !p.toLowerCase().includes('dewasa'));
        }
        return { ...en, plans: updatedPlans };
      }
      return en;
    }));
  };

  const handleToggleNewPlan = (planName: string) => {
    setNewEnrollmentPlans(prev => {
      const hasPlan = prev.includes(planName);
      let updated = hasPlan
        ? prev.filter(p => p !== planName)
        : [...prev, planName];

      if (planName.toLowerCase().includes('dewasa') && !hasPlan) {
        updated = updated.filter(p => !p.toLowerCase().includes('muda'));
      } else if (planName.toLowerCase().includes('muda') && !hasPlan) {
        updated = updated.filter(p => !p.toLowerCase().includes('dewasa'));
      }
      return updated;
    });
  };

  const handleSaveEnrollments = async () => {
    if (!selectedEnrollmentUser) return;
    try {
      const primaryGelanggang = currentEnrollments.length > 0
        ? currentEnrollments[0].gelanggang as Gelanggang
        : 'Tiada' as Gelanggang;

      const updatedUser: UserProfile = {
        ...selectedEnrollmentUser,
        gelanggang: primaryGelanggang,
        sessionTime: JSON.stringify(currentEnrollments)
      };

      await saveMemberDB(updatedUser);
      if (selectedEnrollmentUser.id === currentUser?.id) {
        setCurrentUser(updatedUser);
      }
      showToast('success', 'Langganan Berjaya Dikemaskini', `Pendaftaran gelanggang & kelas untuk ${selectedEnrollmentUser.name} telah disimpan.`);
      setIsEnrollmentModalOpen(false);
      fetchData();
      window.dispatchEvent(new Event('asmm_db_updated'));
    } catch (err: any) {
      showToast('error', 'Gagal Mengemaskini Langganan', err.message || String(err));
    }
  };


  const handleEditPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingChild) return;

    if (!file.type.startsWith('image/')) {
      showToast('error', 'Fail Tidak Sah', 'Sila pilih fail imej (PNG, JPG, JPEG, WEBP).');
      return;
    }

    setIsUploadingEditPhoto(true);

    try {
      const uploadedDataUrl = await uploadMemberPhotoToAhliDriveJson(file, editIc.trim() || editingChild.icNo, editName.trim() || editingChild.name);
      setEditPhotoUrl(uploadedDataUrl);
      showToast('success', 'Gambar Dimuat Naik', 'Gambar profil anak berjaya dimuat naik ke vault.');
    } catch {
      showToast('error', 'Gagal Muat Naik', 'Gagal memuat naik gambar profil.');
    } finally {
      setIsUploadingEditPhoto(false);
    }
  };



  // Fetch children & gelanggang list
  const fetchData = React.useCallback(() => {
    if (!currentUser) return;
    setIsLoading(true);
    Promise.all([getMembersDB(), getGelanggangDB()])
      .then(([members, gels]) => {
        const myChildren = members.filter(m => isChildOfParent(m, currentUser));
        setChildren(myChildren);
        setGelanggangList(gels);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [currentUser]);

  // Navigate back to keluarga list
  const navigateToList = React.useCallback(() => {
    setEditingChild(null);
    window.history.pushState(null, '', '/keluarga');
    restoreViewScroll('keluarga_list');
  }, []);

  // Detect /keluarga/edit/{id} from URL and auto-open edit form
  const checkEditRoute = React.useCallback((membersList: UserProfile[]) => {
    const path = window.location.pathname;
    const match = path.match(/^\/keluarga\/edit\/(.+)$/i);
    if (match) {
      const editId = decodeURIComponent(match[1]);
      const child = membersList.find(m => m.id.toLowerCase() === editId.toLowerCase());
      if (child) {
        setEditingChild(child);
        setEditName(child.name);
        setEditIc(child.icNo);
        setEditPhone(child.phone || '');
        setEditEmail(child.email || '');
        setEditAddress(child.address || '');
        setEditGelanggang(child.gelanggang);
        setEditSessionTime(child.sessionTime || '');
        setEditPhotoUrl(child.photoUrl || '/default-avatar.svg');
        setEditRelationship(child.relationship || 'Anak');
        setIsUploadingEditPhoto(false);
      }
    } else {
      setEditingChild(null);
    }
  }, []);

  React.useEffect(() => {
    if (!currentUser) return;
    setIsLoading(true);
    Promise.all([getMembersDB(), getGelanggangDB()])
      .then(([members, gels]) => {
        const myChildren = members.filter(m => isChildOfParent(m, currentUser));
        setChildren(myChildren);
        setGelanggangList(gels);
        setIsLoading(false);
        checkEditRoute(myChildren);
      })
      .catch(() => {
        setIsLoading(false);
      });

    const handleDbUpdate = () => fetchData();
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path.startsWith('/keluarga/edit/')) {
        Promise.all([getMembersDB(), getGelanggangDB()]).then(([members, gels]) => {
          const myChildren = members.filter(m => isChildOfParent(m, currentUser));
          setChildren(myChildren);
          setGelanggangList(gels);
          checkEditRoute(myChildren);
        });
      } else if (path === '/keluarga') {
        setEditingChild(null);
      }
    };

    window.addEventListener('asmm_db_updated', handleDbUpdate);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('asmm_db_updated', handleDbUpdate);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentUser, fetchData, checkEditRoute]);

  // Handlers
  const handleAddChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const cleanIc = sanitizeIc(addChildIc);
    const cleanPhone = sanitizePhone(addChildPhone);
    const cleanEmail = addChildEmail.trim().toLowerCase();

    const parentIc = sanitizeIc(currentUser.icNo || '');
    const parentPhone = sanitizePhone(currentUser.phone || '');

    if (!addChildName.trim() || !cleanIc || !addChildAddress.trim()) {
      showToast('error', 'Maklumat Tidak Lengkap', 'Nama penuh, No. IC dan alamat kediaman diperlukan.');
      return;
    }

    if (cleanIc === parentIc) {
      showToast('error', 'No. IC Bertindih', 'No. Kad Pengenalan ahli tanggungan tidak boleh sama dengan No. IC Waris.');
      return;
    }

    if (cleanPhone && parentPhone && cleanPhone === parentPhone) {
      showToast('error', 'No. Telefon Bertindih', 'No. telefon tidak boleh sama dengan no. telefon waris. Kosongkan jika pesilat belum ada telefon sendiri.');
      return;
    }

    if (cleanEmail && !isValidEmail(cleanEmail)) {
      showToast('error', 'Format Emel Tidak Sah', 'Sila masukkan format alamat emel yang sah (contoh: anak@gmail.com).');
      return;
    }

    try {
      const existing = await getMembersDB();
      const duplicate = findDuplicateIcMember(cleanIc, undefined, existing);

      if (duplicate) {
        showToast('error', 'No. IC Telah Wujud', `No. IC ${addChildIc} sudah berdaftar (${duplicate.name} - ${duplicate.id}).`);
        return;
      }

      if (cleanPhone) {
        const duplicatePhone = findDuplicatePhoneMember(cleanPhone, undefined, existing);
        if (duplicatePhone) {
          showToast('error', 'No. Telefon Telah Digunakan', `No. telefon ini sudah didaftarkan kepada ahli lain (${duplicatePhone.name} - ${duplicatePhone.id}).`);
          return;
        }
      }

      if (cleanEmail) {
        const duplicateEmail = existing.find(m => m.email && m.email.toLowerCase().trim() === cleanEmail);
        if (duplicateEmail) {
          showToast('error', 'Emel Telah Digunakan', 'Emel ini sudah berdaftar.');
          return;
        }
      }

      const newChildId = await getNextMemberId();

      const newChild: UserProfile = {
        id: newChildId,
        name: toTitleCase(addChildName.trim()),
        icNo: cleanIc,
        email: cleanEmail || `${cleanIc}@asmm.org.my`,
        phone: cleanPhone || '',
        address: addChildAddress.trim(),
        role: 'Ahli',
        gelanggang: addChildGelanggang,
        bengkung: 'Putih',
        bengkungBadge: '/badges/putih0.png',
        photoUrl: '/default-avatar.svg',

        joinedDate: new Date().toISOString().split('T')[0],
        status: 'Menunggu Pengesahan',
        parentId: currentUser.id,
        relationship: addChildRelationship,
        password: cleanIc,
        sessionTime: 'Semua Sesi',
        isOldMember: false,
        onboardingStatus: 'pending_payment',
        uniformSize: addChildUniformSize,
        kinName: currentUser.name,
        kinPhone: currentUser.phone
      };

      const onbInv = createOnboardingInvoice(newChild, addChildUniformSize);
      await saveInvoiceDB(onbInv);
      newChild.onboardingInvoiceId = onbInv.id;

      await saveMemberDB(newChild);
      showToast(
        'success',
        'Pendaftaran Ahli Baharu Dihantar',
        `Invois onboarding uniform & keahlian untuk ${addChildName} telah dijana dalam portal yuran.`
      );

      setAddChildName('');
      setAddChildIc('');
      setAddChildPhone('');
      setAddChildEmail('');
      setAddChildAddress(currentUser?.address || '');
      setAddChildRelationship('Anak');
      setIsAddChildModalOpen(false);

      fetchData();
      window.dispatchEvent(new Event('asmm_db_updated'));
    } catch {
      showToast('error', 'Gagal mendaftar ahli keluarga');
    }
  };

  const handleLinkChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const cleanIc = sanitizeIc(linkChildIc);
    if (!cleanIc) {
      showToast('error', 'Kad Pengenalan diperlukan');
      return;
    }

    try {
      const existing = await getMembersDB();
      const child = existing.find(m => m.icNo && sanitizeIc(m.icNo) === cleanIc);

      if (!child) {
        showToast('error', 'Kad Pengenalan Tidak Dijumpai', 'Sila pastikan IC betul atau hubungi Ketua Gelanggang.');
        return;
      }

      if (child.parentId) {
        showToast('error', 'Ahli Telah Dihubungkan', 'Pesilat ini sudah dihubungkan.');
        return;
      }

      const updatedChild: UserProfile = {
        ...child,
        parentId: currentUser.id,
        relationship: linkChildRelationship
      };

      await saveMemberDB(updatedChild);
      showToast('success', 'Pautan Berjaya', `Pesilat ${child.name} dihubungkan.`);

      setLinkChildIc('');
      setLinkChildRelationship('Anak');
      setIsLinkChildModalOpen(false);

      fetchData();
      window.dispatchEvent(new Event('asmm_db_updated'));
    } catch {
      showToast('error', 'Gagal menghubungkan ahli keluarga');
    }
  };

  const handleOpenEditChildModal = (child: UserProfile) => {
    saveViewScroll('keluarga_list');
    setEditingChild(child);
    setEditName(child.name);
    setEditIc(child.icNo || '');
    setEditPhone(child.phone || '');
    setEditEmail(child.email || '');
    setEditAddress(child.address || '');
    setEditGelanggang(child.gelanggang);
    setEditSessionTime(child.sessionTime || '');
    setEditPhotoUrl(child.photoUrl || '');
    setEditRelationship(child.relationship || 'Anak');
    setIsUploadingEditPhoto(false);
    window.history.pushState(null, '', `/keluarga/edit/${child.id}`);
  };

  const handleSaveEditChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingChild) return;
    const cleanIc = sanitizeIc(editIc);
    const cleanPhone = sanitizePhone(editPhone);
    const cleanEmail = editEmail.trim().toLowerCase();

    const parentIc = sanitizeIc(currentUser?.icNo || '');
    const parentPhone = sanitizePhone(currentUser?.phone || '');

    if (!editName.trim() || !cleanIc) {
      showToast('error', 'Maklumat Tidak Lengkap', 'Nama penuh dan No. IC diperlukan.');
      return;
    }

    if (cleanIc === parentIc) {
      showToast('error', 'No. IC Bertindih', 'No. Kad Pengenalan ahli tanggungan tidak boleh sama dengan No. IC Waris.');
      return;
    }

    if (cleanPhone && parentPhone && cleanPhone === parentPhone) {
      showToast('error', 'No. Telefon Bertindih', 'No. telefon tidak boleh sama dengan no. telefon waris.');
      return;
    }

    if (cleanEmail && !isValidEmail(cleanEmail)) {
      showToast('error', 'Format Emel Tidak Sah', 'Sila masukkan format alamat emel yang sah.');
      return;
    }

    try {
      const existing = await getMembersDB();

      const duplicateIc = findDuplicateIcMember(cleanIc, editingChild.id, existing);
      if (duplicateIc) {
        showToast('error', 'No. IC Telah Digunakan', `No. IC ini sudah didaftarkan kepada ahli lain (${duplicateIc.name} - ${duplicateIc.id}).`);
        return;
      }

      if (cleanPhone) {
        const duplicatePhone = findDuplicatePhoneMember(cleanPhone, editingChild.id, existing);
        if (duplicatePhone) {
          showToast('error', 'No. Telefon Telah Digunakan', `No. telefon ini sudah digunakan oleh ahli lain (${duplicatePhone.name} - ${duplicatePhone.id}).`);
          return;
        }
      }

      if (cleanEmail) {
        const duplicateEmail = existing.find(m => m.id !== editingChild.id && m.email && m.email.toLowerCase().trim() === cleanEmail);
        if (duplicateEmail) {
          showToast('error', 'Emel Telah Digunakan', 'Emel ini sudah digunakan ahli lain.');
          return;
        }
      }

      const updatedChild: UserProfile = {
        ...editingChild,
        name: toTitleCase(editName.trim()),
        icNo: cleanIc,
        phone: cleanPhone,
        email: cleanEmail,
        address: editAddress.trim(),
        gelanggang: editGelanggang,
        sessionTime: editingChild.sessionTime || 'Semua Sesi',
        photoUrl: editPhotoUrl,
        relationship: editRelationship,
        parentId: editingChild.parentId || (currentUser?.id ? currentUser.id : 'ASMM-011')
      };

      await saveMemberDB(updatedChild);
      showToast('success', 'Kemaskini Berjaya', `Profil ${editName} disimpan.`);
      navigateToList();
      fetchData();
      window.dispatchEvent(new Event('asmm_db_updated'));
    } catch (err: any) {
      console.error('Error saving child profile:', err);
      showToast('error', 'Gagal mengemaskini profil', err?.message || String(err));
    }
  };

  const getBengkungShort = (bengkung: string): string => {
    const parts = bengkung.split('(');
    return parts[0].trim();
  };

  return (
    <div className="space-y-6">
      {editingChild ? (
        <div className="space-y-6">
          {/* Back button */}
          <button
            onClick={() => navigateToList()}
            className="flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-asmm-red transition-colors cursor-pointer bg-transparent border-none p-0"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Kembali ke Senarai Keluarga</span>
          </button>

          {/* PAGE HEADER */}
          <PageHeader
            badgeText={`ID Ahli: ${sanitizeMemberId(editingChild.id)}`}
            badgeIcon={Users}
            title={`Profil: ${editingChild.name}`}
          />

          {/* EDIT FORM CONTAINER */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <form onSubmit={handleSaveEditChild} className="space-y-5">
              {/* Photo Upload Section */}
              <div className="flex flex-col items-center sm:items-start gap-3 pb-5 border-b border-slate-100">
                <span className="block text-xs font-bold text-slate-700">Gambar Profil</span>
                <div className="flex items-center gap-4">
                  <div
                    className="relative shrink-0 group cursor-pointer"
                    onClick={() => editPhotoInputRef.current?.click()}
                    title="Klik untuk muat naik gambar profil baru"
                  >
                    <img
                      src={editPhotoUrl || '/default-avatar.svg'}
                      alt="Pratinjau Foto"
                      className="w-20 h-20 rounded-full object-cover border-2 border-slate-200 shadow-sm transition-opacity group-hover:opacity-80"
                      onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                    />
                    <div className="absolute inset-0 bg-slate-900/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera className="w-5 h-5 text-white" />
                    </div>
                    {isUploadingEditPhoto ? (
                      <div className="absolute inset-0 bg-slate-900/70 rounded-full flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                      </div>
                    ) : (
                      <span className="absolute bottom-0 right-0 w-5 h-5 bg-asmm-red text-white border-2 border-white rounded-full flex items-center justify-center shadow-xs">
                        <Upload className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 text-center sm:text-left">
                    <button
                      type="button"
                      onClick={() => editPhotoInputRef.current?.click()}
                      className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-[10px] rounded-xl transition-all cursor-pointer border-none shadow-xs"
                    >
                      Muat Naik Gambar Baru
                    </button>
                    <p className="text-[10px] text-slate-500">Imej PNG, JPG, JPEG, WEBP sahaja. Gambar disimpan dalam Google Drive vault.</p>
                  </div>
                </div>
                {/* Hidden input */}
                <input
                  type="file"
                  ref={editPhotoInputRef}
                  onChange={handleEditPhotoUpload}
                  accept="image/*"
                  className="hidden"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Nama Penuh *</label>
                <input
                  type="text"
                  required
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Contoh: Amirul Bin Ahmad Zaki"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">No. Kad Pengenalan *</label>
                  <input
                    type="text"
                    required
                    value={editIc}
                    onChange={(e) => setEditIc(e.target.value)}
                    placeholder="100514-02-5201"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">No. Telefon</label>
                  <input
                    type="text"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    placeholder="019-4920194"
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Alamat Emel</label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="amirul@gmail.com"
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Alamat Kediaman</label>
                <textarea
                  rows={2}
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                  placeholder="Masukkan alamat kediaman..."
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium resize-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Hubungan Keluarga *</label>
                  <select
                    value={editRelationship}
                    onChange={(e) => setEditRelationship(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                  >
                    <option value="Anak">Anak</option>
                    <option value="Isteri">Isteri</option>
                    <option value="Suami">Suami</option>
                    <option value="Bapa">Bapa</option>
                    <option value="Ibu">Ibu</option>
                    <option value="Adik-beradik">Adik-beradik</option>
                    <option value="Bapa Saudara">Bapa Saudara</option>
                    <option value="Ibu Saudara">Ibu Saudara</option>
                    <option value="Lain-lain">Lain-lain</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">Gelanggang Latihan *</label>
                  <select
                    value={editGelanggang}
                    onChange={(e) => setEditGelanggang(e.target.value as Gelanggang)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                  >
                    <option value="Pusaka Warisan">Pusaka Warisan</option>
                    <option value="Wadi AnNour">Wadi AnNour</option>
                    <option value="Paya Besar">Paya Besar</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => navigateToList()}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-transparent border-none cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer border-none"
                >
                  <Save className="w-3.5 h-3.5 text-amber-400" />
                  <span>Simpan Perubahan Profil</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <>
          {/* PAGE HEADER */}
          <PageHeader
            badgeText="Pengurusan Keluarga & Waris"
            badgeIcon={Users}
            title="Keluarga Saya"
            titleBadge={1 + children.length}
          >
            <button
              onClick={() => setIsAddChildModalOpen(true)}
              className="px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer border-none"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Tambah Baru</span>
            </button>
            <button
              onClick={() => setIsLinkChildModalOpen(true)}
              className="px-3.5 py-2 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-xl transition-all border border-slate-200 shadow-sm flex items-center gap-1.5 cursor-pointer"
            >
              <LinkIcon className="w-3.5 h-3.5 text-asmm-red" />
              <span>Hubung Ahli Keluarga</span>
            </button>
          </PageHeader>

          {/* SUMMARY OVERVIEW BAR */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-xs flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold text-slate-500">Ahli Keluarga Berdaftar</div>
                <div className="text-base font-extrabold text-slate-900">
                  {1 + children.length} <span className="text-xs font-semibold text-slate-500">Orang</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-xs flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold text-slate-500 flex items-center gap-1.5">
                  <span>Komitmen Yuran Bulanan</span>
                </div>
                <div className="text-base font-black text-emerald-700">
                  RM{totalFamilyMonthlyFee} <span className="text-xs font-semibold text-slate-500">/ bulan</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-xs flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <Building className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-bold text-slate-500">Gelanggang Latihan Utama</div>
                <div className="text-sm font-extrabold text-slate-900 truncate">
                  {currentUser?.gelanggang && currentUser.gelanggang !== 'Tiada' ? currentUser.gelanggang : 'Pusaka Warisan'}
                </div>
              </div>
            </div>
          </div>

          {/* MEMBERS LIST VIEW */}
          <div className="flex flex-col gap-5">
            {/* SELF PROFILE (WARIS / ANDA) */}
            {currentUser && (
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col justify-between border-l-4 border-l-indigo-600">
                <div className="p-6 space-y-5">
                  {/* Header profile info */}
                  <div className="flex items-start gap-4">
                    <img
                      src={currentUser.photoUrl || '/default-avatar.svg'}
                      alt={currentUser.name}
                      className="w-16 h-16 rounded-2xl object-cover border-2 border-indigo-100 shadow-xs shrink-0"
                      onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                    />
                    <div className="space-y-1.5 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-base font-extrabold text-slate-900 truncate">{currentUser.name}</h4>
                        <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 tracking-wide">
                          {getHighestRole(currentUser)} (Anda)
                        </span>
                        <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {currentUser.status || 'Aktif'}
                        </span>
                      </div>

                      {/* Rank details */}
                      {(currentUser.role !== 'Waris' || (currentUser.roles && currentUser.roles.some(r => r !== 'Waris')) || (currentUser as any).isAlsoPesilat || isSuperAdmin(currentUser)) && currentUser.bengkung ? (
                        <div className="flex items-center gap-2.5 flex-wrap pt-0.5">
                          <BengkungCulaBadge bengkung={currentUser.bengkung} size="sm" fullWidth={false} />
                          <span className="text-xs font-extrabold text-asmm-red">
                            {getSilatRankFromBengkung(currentUser.bengkung)}
                          </span>
                        </div>
                      ) : (
                        <div className="text-[11px] font-medium text-slate-500">
                          Ibu Bapa / Penjaga Pesilat (Bukan Pesilat)
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attributes list */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-xs border-t border-slate-100">
                    <div className="flex items-center gap-2 text-slate-600 font-medium">
                      <Fingerprint className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-bold text-slate-700">No. IC:</span>
                      <span className="font-mono text-slate-800">{currentUser.icNo}</span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-600 font-medium">
                      <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-bold text-slate-700">Ahli Sejak:</span>
                      <span className="font-mono text-slate-800">{currentUser.joinedDate}</span>
                    </div>

                    {currentUser.phone && currentUser.phone !== '019-0000000' && (
                      <div className="flex items-center gap-2 text-slate-600 font-medium">
                        <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="font-bold text-slate-700">Telefon:</span>
                        <span className="font-mono text-slate-800">{currentUser.phone}</span>
                      </div>
                    )}

                    {currentUser.email && !currentUser.email.endsWith('@asmm.org.my') && (
                      <div className="flex items-center gap-2 text-slate-600 font-medium">
                        <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="font-bold text-slate-700">Emel:</span>
                        <span className="truncate text-slate-800">{currentUser.email}</span>
                      </div>
                    )}
                  </div>

                  {/* DEDICATED LANNGANAN & YURAN SUB-PANEL */}
                  <div className="bg-slate-50/90 border border-slate-200/80 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                          <Building className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-xs font-extrabold text-slate-900">Pendaftaran Gelanggang & Kelas</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenEnrollmentModal(currentUser)}
                        className="px-3 py-1 bg-white hover:bg-slate-100 text-indigo-700 text-[11px] font-extrabold rounded-lg border border-slate-200 shadow-2xs transition-all flex items-center gap-1 cursor-pointer"
                      >
                        <Plus className="w-3 h-3 text-indigo-600" />
                        <span>Urus Kelas</span>
                      </button>
                    </div>

                    {parseEnrollments(currentUser).length > 0 ? (
                      <div className="space-y-2">
                        {parseEnrollments(currentUser).map((en, idx) => {
                          const isEligible = isEligibleForMonthlyFee(currentUser);
                          const exemptionReason = getMonthlyFeeExemptionReason(currentUser);
                          const totalEnPrice = en.plans.reduce((sum, p) => sum + getPlanPrice(en.gelanggang, p), 0);
                          return (
                            <div key={idx} className="bg-white rounded-xl p-3 border border-slate-200/70 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                              <div className="space-y-1">
                                <div className="text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                                  <MapPin className="w-3.5 h-3.5 text-asmm-red shrink-0" />
                                  <span>{en.gelanggang}</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {en.plans.map((p, pIdx) => {
                                    const price = getPlanPrice(en.gelanggang, p);
                                    return (
                                      <span key={pIdx} className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 px-2.5 py-0.5 rounded-lg text-[11px] font-bold">
                                        <span>{p}</span>
                                        <span className={isEligible ? "text-emerald-700 font-extrabold" : "text-slate-400 font-medium line-through"}>• RM{price}/bln</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="text-left sm:text-right shrink-0">
                                <span className="text-[10px] text-slate-400 font-bold block">Anggaran Yuran Bulanan</span>
                                {isEligible ? (
                                  <span className="text-xs font-black text-emerald-700">RM{totalEnPrice} / bulan</span>
                                ) : (
                                  <div className="flex items-center gap-1.5 sm:justify-end">
                                    <span className="text-xs font-black text-emerald-700">RM0 / bulan</span>
                                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      {exemptionReason || 'Dikecualikan'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-500 italic py-2 text-center bg-white rounded-xl border border-dashed border-slate-200">
                        Belum ada kelas berdaftar. Klik butang <strong>Urus Kelas</strong> untuk mendaftar kelas.
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-6 py-3.5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="font-mono font-semibold">ID: {sanitizeMemberId(currentUser.id)}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        window.history.pushState(null, '', '/account');
                        window.dispatchEvent(new PopStateEvent('popstate'));
                      }}
                      className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 font-bold text-[11px] rounded-lg border border-slate-200 transition-all cursor-pointer flex items-center gap-1 shadow-2xs"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                      <span>Kemaskini Profil</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* CHILDREN & FAMILY MEMBERS */}
            {children.map(child => (
              <div key={child.id} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col justify-between border-l-4 border-l-asmm-red">
                <div className="p-6 space-y-5">
                  {/* Header profile info */}
                  <div className="flex items-start gap-4">
                    <img
                      src={child.photoUrl || '/default-avatar.svg'}
                      alt={child.name}
                      className="w-16 h-16 rounded-2xl object-cover border-2 border-red-100 shadow-xs shrink-0"
                      onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                    />
                    <div className="space-y-1.5 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-base font-extrabold text-slate-900 truncate">{child.name}</h4>
                        <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-asmm-red/10 text-asmm-red border border-asmm-red/20 tracking-wide">
                          {child.relationship || 'Anak'}{getIcDetails(child.icNo) ? ` • ${getIcDetails(child.icNo)?.age} Tahun` : ''}
                        </span>
                        <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full ${child.status === 'Aktif'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}>
                          {child.status === 'Aktif' ? 'Aktif' : 'Semakan Waris'}
                        </span>
                      </div>

                      {/* Rank details */}
                      <div className="flex items-center gap-2.5 flex-wrap pt-0.5">
                        <BengkungCulaBadge bengkung={child.bengkung} size="sm" fullWidth={false} />
                        <span className="text-xs font-extrabold text-asmm-red">
                          {getSilatRankFromBengkung(child.bengkung)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Attributes list */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-xs border-t border-slate-100">
                    <div className="flex items-center gap-2 text-slate-600 font-medium">
                      <Fingerprint className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-bold text-slate-700">No. IC:</span>
                      <span className="font-mono text-slate-800">{child.icNo}</span>
                    </div>

                    <div className="flex items-center gap-2 text-slate-600 font-medium">
                      <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-bold text-slate-700">Ahli Sejak:</span>
                      <span className="font-mono text-slate-800">{child.joinedDate}</span>
                    </div>

                    {child.phone && child.phone !== '019-0000000' && (
                      <div className="flex items-center gap-2 text-slate-600 font-medium">
                        <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="font-bold text-slate-700">Telefon:</span>
                        <span className="font-mono text-slate-800">{child.phone}</span>
                      </div>
                    )}

                    {child.address && (
                      <div className="flex items-center gap-2 text-slate-600 font-medium col-span-1 sm:col-span-2">
                        <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="font-bold text-slate-700">Alamat:</span>
                        <span className="truncate text-slate-800">{child.address}</span>
                      </div>
                    )}
                  </div>

                  {/* DEDICATED LANNGANAN & YURAN SUB-PANEL */}
                  <div className="bg-slate-50/90 border border-slate-200/80 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-asmm-red/10 text-asmm-red flex items-center justify-center shrink-0">
                          <Building className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-xs font-extrabold text-slate-900">Pendaftaran Gelanggang & Kelas</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenEnrollmentModal(child)}
                        className="px-3 py-1 bg-white hover:bg-slate-100 text-asmm-red text-[11px] font-extrabold rounded-lg border border-slate-200 shadow-2xs transition-all flex items-center gap-1 cursor-pointer"
                      >
                        <Plus className="w-3 h-3 text-asmm-red" />
                        <span>Urus Kelas</span>
                      </button>
                    </div>

                    {parseEnrollments(child).length > 0 ? (
                      <div className="space-y-2">
                        {parseEnrollments(child).map((en, idx) => {
                          const isEligible = isEligibleForMonthlyFee(child);
                          const exemptionReason = getMonthlyFeeExemptionReason(child);
                          const totalEnPrice = en.plans.reduce((sum, p) => sum + getPlanPrice(en.gelanggang, p), 0);
                          return (
                            <div key={idx} className="bg-white rounded-xl p-3 border border-slate-200/70 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                              <div className="space-y-1">
                                <div className="text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                                  <MapPin className="w-3.5 h-3.5 text-asmm-red shrink-0" />
                                  <span>{en.gelanggang}</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {en.plans.map((p, pIdx) => {
                                    const price = getPlanPrice(en.gelanggang, p);
                                    return (
                                      <span key={pIdx} className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 px-2.5 py-0.5 rounded-lg text-[11px] font-bold">
                                        <span>{p}</span>
                                        <span className={isEligible ? "text-emerald-700 font-extrabold" : "text-slate-400 font-medium line-through"}>• RM{price}/bln</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="text-left sm:text-right shrink-0">
                                <span className="text-[10px] text-slate-400 font-bold block">Anggaran Yuran Bulanan</span>
                                {isEligible ? (
                                  <span className="text-xs font-black text-emerald-700">RM{totalEnPrice} / bulan</span>
                                ) : (
                                  <div className="flex items-center gap-1.5 sm:justify-end">
                                    <span className="text-xs font-black text-emerald-700">RM0 / bulan</span>
                                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      {exemptionReason || 'Dikecualikan'}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-500 italic py-2 text-center bg-white rounded-xl border border-dashed border-slate-200">
                        Belum ada kelas berdaftar. Klik butang <strong>Urus Kelas</strong> untuk mendaftar kelas.
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-6 py-3.5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="font-mono font-semibold">ID: {sanitizeMemberId(child.id)}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleOpenEditChildModal(child)}
                      className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 font-bold text-[11px] rounded-lg border border-slate-200 transition-all cursor-pointer flex items-center gap-1 shadow-2xs"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                      <span>Kemaskini Profil</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* EMPTY STATE IF NO CHILDREN */}
            {children.length === 0 && (
              <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-8 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-asmm-red/10 text-asmm-red flex items-center justify-center mx-auto">
                  <Users className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-extrabold text-slate-900">Belum Ada Profil Ahli Keluarga Didaftarkan</h4>
                  <p className="text-xs text-slate-500 max-w-md mx-auto">
                    Daftarkan profil anak atau tanggungan anda untuk memudahkan pengurusan pendaftaran kelas, pemantauan yuran dan kenaikan bengkung.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 pt-2">
                  <button
                    onClick={() => setIsAddChildModalOpen(true)}
                    className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer border-none"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Daftar Ahli Keluarga Sekarang</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* MODALS */}

      {/* DAFTAR AHLI KELUARGA BARU MODAL */}
      <Modal
        isOpen={isAddChildModalOpen}
        onClose={() => setIsAddChildModalOpen(false)}
        title="Daftar Profil Ahli Keluarga Baharu"
      >
        <form onSubmit={handleAddChild} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Nama Penuh *</label>
            <input
              type="text"
              required
              value={addChildName}
              onChange={(e) => setAddChildName(e.target.value)}
              placeholder="Contoh: Norhaliza Binti Ahmad Zaki"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">No. Kad Pengenalan *</label>
              <input
                type="text"
                required
                value={addChildIc}
                onChange={(e) => setAddChildIc(e.target.value)}
                placeholder="100514-02-5201"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">No. Telefon</label>
              <input
                type="text"
                value={addChildPhone}
                onChange={(e) => setAddChildPhone(e.target.value)}
                placeholder="019-4920194 (Opsional)"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Alamat Emel</label>
            <input
              type="email"
              value={addChildEmail}
              onChange={(e) => setAddChildEmail(e.target.value)}
              placeholder="norhaliza@gmail.com (Opsional)"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Alamat Kediaman *</label>
            <textarea
              rows={2}
              required
              value={addChildAddress}
              onChange={(e) => setAddChildAddress(e.target.value)}
              placeholder="Contoh: No 12, Jalan Sejahtera 3, Taman Sejahtera, 09600 Lunas, Kedah"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Hubungan Keluarga *</label>
              <select
                value={addChildRelationship}
                onChange={(e) => setAddChildRelationship(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
              >
                <option value="Anak">Anak</option>
                <option value="Isteri">Isteri</option>
                <option value="Suami">Suami</option>
                <option value="Bapa">Bapa</option>
                <option value="Ibu">Ibu</option>
                <option value="Adik-beradik">Adik-beradik</option>
                <option value="Bapa Saudara">Bapa Saudara</option>
                <option value="Ibu Saudara">Ibu Saudara</option>
                <option value="Lain-lain">Lain-lain</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Gelanggang Latihan *</label>
              <select
                value={addChildGelanggang}
                onChange={(e) => setAddChildGelanggang(e.target.value as Gelanggang)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
              >
                <option value="Tiada">Tiada (Belum Ditetapkan)</option>
                <option value="Pusaka Warisan">Pusaka Warisan</option>
                <option value="Wadi AnNour">Wadi AnNour</option>
                <option value="Paya Besar">Paya Besar</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <span>Saiz Uniform Silat (Ketinggian) *</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsSizeChartOpen(true)}
                  className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1 text-xs font-bold cursor-pointer bg-transparent border-none p-0"
                  title="Buka Carta Cadangan Saiz"
                >
                  <Ruler className="w-3.5 h-3.5" />
                  <span>Carta Saiz</span>
                </button>
              </div>
              <select
                value={addChildUniformSize}
                onChange={(e) => setAddChildUniformSize(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
              >
                {UNIFORM_SIZES.map(s => (
                  <option key={s} value={s}>
                    {s} — RM {getUniformPriceBySize(s)} ({parseInt(s) <= 130 ? 'Kanak-kanak' : parseInt(s) <= 170 ? 'Remaja / Dewasa' : 'Tinggi / Dewasa'})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer border-none"
          >
            <Plus className="w-4 h-4" />
            <span>Hantar Pendaftaran</span>
          </button>
        </form>
      </Modal>

      {/* HUBUNG AHLI KELUARGA SEDIA ADA MODAL */}
      <Modal
        isOpen={isLinkChildModalOpen}
        onClose={() => setIsLinkChildModalOpen(false)}
        title="Hubung Profil Ahli Keluarga Sedia Ada"
      >
        <form onSubmit={handleLinkChild} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">No. Kad Pengenalan *</label>
            <input
              type="text"
              required
              value={linkChildIc}
              onChange={(e) => setLinkChildIc(e.target.value)}
              placeholder="Contoh: 030819-02-5819"
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
            />
            <p className="text-[10px] text-slate-500 mt-1">Masukkan No. Kad Pengenalan ahli keluarga anda yang telah didaftarkan oleh Ketua Gelanggang untuk dihubungkan ke portal anda.</p>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Hubungan Keluarga *</label>
            <select
              value={linkChildRelationship}
              onChange={(e) => setLinkChildRelationship(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
            >
              <option value="Anak">Anak</option>
              <option value="Isteri">Isteri</option>
              <option value="Suami">Suami</option>
              <option value="Bapa">Bapa</option>
              <option value="Ibu">Ibu</option>
              <option value="Adik-beradik">Adik-beradik</option>
              <option value="Bapa Saudara">Bapa Saudara</option>
              <option value="Ibu Saudara">Ibu Saudara</option>
              <option value="Lain-lain">Lain-lain</option>
            </select>
          </div>

          <button
            type="submit"
            className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer border-none"
          >
            <LinkIcon className="w-4 h-4 text-asmm-red" />
            <span>Hubungkan Akaun</span>
          </button>
        </form>
      </Modal>
      {/* URUS LANGGANAN GELANGGANG & KELAS MODAL */}
      <Modal
        isOpen={isEnrollmentModalOpen}
        onClose={() => setIsEnrollmentModalOpen(false)}
        title={`Urus Kelas: ${selectedEnrollmentUser?.name || 'Ahli'}`}
      >
        <div className="space-y-6">
          {/* Header Info */}
          {selectedEnrollmentUser && (
            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <img
                  src={selectedEnrollmentUser.photoUrl || '/default-avatar.svg'}
                  alt={selectedEnrollmentUser.name}
                  className="w-10 h-10 rounded-xl object-cover border border-slate-200 shrink-0"
                  onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                />
                <div>
                  <div className="text-xs font-extrabold text-slate-900">{selectedEnrollmentUser.name}</div>
                  <div className="text-[10px] text-slate-500 font-mono">ID: {sanitizeMemberId(selectedEnrollmentUser.id)} • IC: {selectedEnrollmentUser.icNo}</div>
                </div>
              </div>
              <span className="text-[10px] font-black px-2.5 py-0.5 rounded-full bg-asmm-red/10 text-asmm-red border border-asmm-red/20">
                {selectedEnrollmentUser.relationship || selectedEnrollmentUser.role}
              </span>
            </div>
          )}

          {/* Current Enrollments */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-700">Langganan Aktif Semasa</span>
              <span className="text-[10px] text-slate-400 font-medium">Tandakan kelas latihan yang disertai</span>
            </div>

            {currentEnrollments.length > 0 ? (
              <div className="space-y-3">
                {currentEnrollments.map((en, idx) => {
                  const gelDetail = gelanggangList.find(g => g.name === en.gelanggang);
                  const offered = parseGelanggangClasses(en.gelanggang, gelDetail?.classes);
                  const enTotal = en.plans.reduce((sub, p) => sub + getPlanPrice(en.gelanggang, p), 0);

                  return (
                    <div key={idx} className="bg-slate-50/80 border border-slate-200 rounded-2xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-asmm-red shrink-0" />
                          <span className="text-xs font-extrabold text-slate-900">{en.gelanggang}</span>
                          <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                            RM{enTotal}/bln
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveEnrollment(en.gelanggang)}
                          className="p-1.5 text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-lg transition-all cursor-pointer text-[10px] font-bold flex items-center gap-1"
                          title="Padam Langganan Gelanggang"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Padam</span>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {offered.map((cls) => {
                          const isChecked = en.plans.includes(cls.name);
                          return (
                            <label
                              key={cls.name}
                              className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer select-none transition-all ${isChecked
                                ? 'bg-red-50/50 border-asmm-red/40 text-slate-900 shadow-2xs'
                                : 'bg-white border-slate-200 hover:border-slate-300 text-slate-600'
                                }`}
                            >
                              <div className="flex items-center gap-2.5">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleTogglePlanInEnrollment(en.gelanggang, cls.name)}
                                  className="rounded border-slate-300 text-asmm-red focus:ring-asmm-red w-4 h-4 cursor-pointer"
                                />
                                <span className="text-xs font-bold">{cls.name}</span>
                              </div>
                              <span className="text-xs font-black text-emerald-700">RM{cls.price}/bln</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[11px] text-slate-500 italic bg-slate-50 p-4 rounded-2xl border border-dashed border-slate-200 text-center">
                Tiada langganan gelanggang aktif. Sila tambah langganan baharu di bawah.
              </p>
            )}
          </div>

          {/* Add New Enrollment */}
          <div className="border-t border-slate-100 pt-4 space-y-4">
            <span className="block text-xs font-bold text-slate-700">Daftar Gelanggang Baharu</span>
            <div className="bg-slate-50/70 p-4 rounded-2xl border border-slate-200 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Pilih Gelanggang *</label>
                <select
                  value={newEnrollmentGelanggang}
                  onChange={(e) => {
                    const nextG = e.target.value as Gelanggang;
                    setNewEnrollmentGelanggang(nextG);
                    const classes = parseGelanggangClasses(
                      nextG,
                      gelanggangList.find(g => g.name === nextG)?.classes
                    );
                    if (classes.length > 0) {
                      setNewEnrollmentPlans([classes[0].name]);
                    } else {
                      setNewEnrollmentPlans(['Silat']);
                    }
                  }}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:outline-none text-xs text-slate-900 font-medium cursor-pointer"
                >
                  {gelanggangList
                    .filter(g => g.name !== 'Tiada' && g.name !== 'Semua Gelanggang')
                    .map(g => (
                      <option key={g.id || g.name} value={g.name}>{g.name}</option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1.5">Pilih Kelas / Pelan Ditawarkan *</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {parseGelanggangClasses(
                    newEnrollmentGelanggang,
                    gelanggangList.find(g => g.name === newEnrollmentGelanggang)?.classes
                  ).map((cls) => {
                    const isChecked = newEnrollmentPlans.includes(cls.name);
                    return (
                      <label
                        key={cls.name}
                        className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer select-none transition-all ${isChecked
                          ? 'bg-red-50/50 border-asmm-red/40 text-slate-900 shadow-2xs'
                          : 'bg-white border-slate-200 hover:border-slate-300 text-slate-600'
                          }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleNewPlan(cls.name)}
                            className="rounded border-slate-300 text-asmm-red focus:ring-asmm-red w-4 h-4 cursor-pointer"
                          />
                          <span className="text-xs font-bold">{cls.name}</span>
                        </div>
                        <span className="text-xs font-black text-emerald-700">RM{cls.price}/bln</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddEnrollment}
                className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer border-none"
              >
                <Plus className="w-3.5 h-3.5 text-amber-400" />
                <span>Tambah Gelanggang Ini</span>
              </button>
            </div>
          </div>

          {/* Total Summary */}
          {currentEnrollments.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between text-xs">
              <span className="font-extrabold text-emerald-900">Jumlah Anggaran Yuran Bulanan:</span>
              <span className="font-black text-emerald-800 text-sm">
                RM{currentEnrollments.reduce((sum, en) => sum + en.plans.reduce((sub, p) => sub + getPlanPrice(en.gelanggang, p), 0), 0)} / bulan
              </span>
            </div>
          )}

          {/* Action buttons */}
          <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setIsEnrollmentModalOpen(false)}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-transparent border-none cursor-pointer"
            >
              Batal
            </button>
            <button
              type="button"
              onClick={handleSaveEnrollments}
              className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer border-none"
            >
              <Save className="w-3.5 h-3.5 text-white" />
              <span>Simpan Perubahan Langganan</span>
            </button>
          </div>
        </div>
      </Modal>

      {/* Uniform Size Chart Modal Popup */}
      <UniformSizeChartModal
        isOpen={isSizeChartOpen}
        onClose={() => setIsSizeChartOpen(false)}
        selectedSize={addChildUniformSize}
        onSelectSize={(s) => setAddChildUniformSize(s)}
      />
    </div>
  );
};
